import React, { useCallback, useEffect, useMemo, useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Card, CardContent } from "@/components/ui/card";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { ListChecks, Plus, FolderKanban, LifeBuoy, FileText } from "lucide-react";
import { Link } from "react-router-dom";
import TaskTree from "@/components/TaskTree";
import TaskDialog from "@/components/TaskDialog";
import { TASK_STATUSES } from "@/lib/taskman";

export default function TaskManagerPage() {
  const [tasks, setTasks] = useState([]);
  const [clients, setClients] = useState([]);
  const [projects, setProjects] = useState([]);
  const [proposals, setProposals] = useState([]);
  const [assignees, setAssignees] = useState({ team: [], client_users: [] });
  const [loading, setLoading] = useState(true);
  const [clientFilter, setClientFilter] = useState("all");
  const [statusFilter, setStatusFilter] = useState("all");
  const [search, setSearch] = useState("");
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [parentTask, setParentTask] = useState(null);

  const load = useCallback(async () => {
    try {
      const params = clientFilter !== "all" ? { client_id: clientFilter } : {};
      const [t, c, p, a, pp] = await Promise.all([
        api.get("/tasks", { params }),
        api.get("/clients"),
        api.get("/projects"),
        api.get("/taskman/assignees"),
        api.get("/proposals-pipeline"),
      ]);
      setTasks(t.data.tasks || []);
      setClients(Array.isArray(c.data) ? c.data : []);
      setProjects(p.data.projects || []);
      setAssignees(a.data);
      setProposals(pp.data.proposals || []);
    } catch (e) {
      toast.error("Failed to load tasks");
    } finally {
      setLoading(false);
    }
  }, [clientFilter]);

  useEffect(() => { load(); }, [load]);

  const filtered = useMemo(() => {
    let rows = tasks;
    if (statusFilter !== "all") rows = rows.filter((t) => t.status === statusFilter);
    if (search.trim()) {
      const q = search.toLowerCase();
      rows = rows.filter((t) => (t.title || "").toLowerCase().includes(q));
    }
    // re-root orphaned children so filtered trees still render
    const ids = new Set(rows.map((t) => t.task_id));
    return rows.map((t) =>
      t.parent_task_id && !ids.has(t.parent_task_id)
        ? { ...t, parent_task_id: null } : t);
  }, [tasks, statusFilter, search]);

  const groups = useMemo(() => {
    const map = new Map();
    filtered.forEach((t) => {
      const key = t.ticket_id ? `ticket:${t.ticket_id}`
        : t.proposal_id && !t.project_id ? `proposal:${t.proposal_id}`
        : `project:${t.project_id || "none"}`;
      if (!map.has(key)) {
        map.set(key, {
          key,
          kind: t.ticket_id ? "ticket" : t.proposal_id && !t.project_id ? "proposal" : "project",
          id: t.ticket_id || t.project_id,
          label: t.ticket_id ? (t.ticket_subject || "Ticket")
            : t.proposal_id && !t.project_id ? `${t.proposal_title || "Proposal"} (forecast)`
            : t.project_name || "Unassigned",
          client: t.client_name, rows: [],
        });
      }
      map.get(key).rows.push(t);
    });
    return [...map.values()];
  }, [filtered]);

  const saveTask = async (payload) => {
    try {
      if (editing?.task_id) {
        await api.patch(`/tasks/${editing.task_id}`, payload);
        toast.success("Task updated");
      } else {
        if (parentTask) payload.parent_task_id = parentTask.task_id;
        await api.post("/tasks", payload);
        toast.success("Task created");
      }
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Save failed");
      throw e;
    }
  };

  const quickStatus = async (task, status) => {
    try {
      await api.patch(`/tasks/${task.task_id}`, { status });
      setTasks((rows) => rows.map((r) => r.task_id === task.task_id ? { ...r, status } : r));
    } catch { toast.error("Update failed"); }
  };

  const deleteTask = async (task) => {
    if (!window.confirm(`Delete "${task.title}" and all its subtasks?`)) return;
    try {
      await api.delete(`/tasks/${task.task_id}`);
      toast.success("Task deleted");
      load();
    } catch { toast.error("Delete failed"); }
  };

  const reorder = async (taskIds) => {
    try {
      await api.post("/tasks/reorder", { task_ids: taskIds });
      load();
    } catch { toast.error("Reorder failed"); }
  };

  const openNew = () => { setEditing(null); setParentTask(null); setDialogOpen(true); };
  const openEdit = (t) => { setEditing(t); setParentTask(null); setDialogOpen(true); };
  const openSub = (t) => { setEditing(null); setParentTask(t); setDialogOpen(true); };

  const stats = useMemo(() => ({
    open: tasks.filter((t) => ["todo", "in_progress", "blocked"].includes(t.status)).length,
    pending: tasks.filter((t) => t.status === "pending").length,
    done: tasks.filter((t) => t.status === "done").length,
  }), [tasks]);

  return (
    <div className="p-6 space-y-4 max-w-[1400px]" data-testid="task-manager-page">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h1 className="text-2xl font-display font-extrabold tracking-tight flex items-center gap-2">
            <ListChecks className="h-6 w-6 text-[#002FA7]" /> Task Manager
          </h1>
          <p className="text-sm text-zinc-500 mt-1">
            All work across projects, tickets and proposals — {stats.open} open · {stats.pending} pending · {stats.done} done
          </p>
        </div>
        <Button onClick={openNew} data-testid="new-task-btn">
          <Plus className="h-4 w-4 mr-1" /> New task
        </Button>
      </div>

      <div className="flex items-center gap-2 flex-wrap">
        <Select value={clientFilter} onValueChange={setClientFilter}>
          <SelectTrigger className="w-56" data-testid="task-filter-client">
            <SelectValue placeholder="All clients" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All clients</SelectItem>
            {clients.map((c) => (
              <SelectItem key={c.client_id} value={c.client_id}>{c.company_name}</SelectItem>
            ))}
          </SelectContent>
        </Select>
        <Select value={statusFilter} onValueChange={setStatusFilter}>
          <SelectTrigger className="w-44" data-testid="task-filter-status">
            <SelectValue placeholder="All statuses" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All statuses</SelectItem>
            {TASK_STATUSES.map((s) => (
              <SelectItem key={s.value} value={s.value}>{s.label}</SelectItem>
            ))}
          </SelectContent>
        </Select>
        <Input className="w-64" placeholder="Search tasks…" value={search}
          onChange={(e) => setSearch(e.target.value)} data-testid="task-search-input" />
      </div>

      {loading ? (
        <div className="p-10 text-center text-sm text-zinc-400">Loading…</div>
      ) : groups.length === 0 ? (
        <Card><CardContent className="p-10 text-center text-sm text-zinc-400" data-testid="tasks-empty">
          No tasks match. Create your first task — it lands in the client's "Do &amp; Charge" project unless you pick a project.
        </CardContent></Card>
      ) : (
        groups.map((g) => (
          <Card key={g.key} data-testid={`task-group-${g.key}`}>
            <CardContent className="p-4">
              <div className="flex items-center gap-2 mb-2">
                {g.kind === "ticket" ? <LifeBuoy className="h-4 w-4 text-amber-600" />
                  : g.kind === "proposal" ? <FileText className="h-4 w-4 text-violet-600" />
                  : <FolderKanban className="h-4 w-4 text-[#002FA7]" />}
                {g.kind === "project" && g.id ? (
                  <Link to={`/projects/${g.id}`} className="font-semibold text-sm hover:underline">{g.label}</Link>
                ) : g.kind === "ticket" ? (
                  <Link to={`/tickets/${g.id}`} className="font-semibold text-sm hover:underline">{g.label}</Link>
                ) : (
                  <span className="font-semibold text-sm">{g.label}</span>
                )}
                <span className="text-xs text-zinc-400">· {g.client}</span>
                <span className="ml-auto text-xs text-zinc-400">{g.rows.length} task{g.rows.length > 1 ? "s" : ""}</span>
              </div>
              <TaskTree tasks={g.rows} onEdit={openEdit} onAddSub={openSub}
                onDelete={deleteTask} onQuickStatus={quickStatus} onReorder={reorder} />
            </CardContent>
          </Card>
        ))
      )}

      <TaskDialog open={dialogOpen} onOpenChange={setDialogOpen}
        initial={editing} onSave={saveTask} assignees={assignees}
        destination={!editing && !parentTask} clients={clients} projects={projects}
        proposals={proposals} />
    </div>
  );
}
